import { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import apiClient from "@/api/client";
import { formatAmount } from "@/utils/formatAmount";
import { triggerBrowserDownload } from "@/utils/download";
import { getRoleLabel } from "@/utils/roleLabel";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";
import { ORDER_STATUS_LABELS, PRIORITY_LABELS, type OrderStatus, type Priority } from "@/features/orders/types";
import type { Project } from "@/features/enterprises/types";
import { ProjectStatusActions } from "@/features/enterprises/components/ProjectStatusActions";
import { ProjectStatusBadge } from "@/features/enterprises/components/ContractStatusBadge";
import { ContractDocumentsSection } from "@/features/enterprises/components/ContractDocumentsSection";
import { ProjectEditForm } from "@/features/enterprises/components/ProjectEditForm";
import { canDeactivateProject, canSetProjectStatus } from "@/features/enterprises/permissions";
import { useAuthStore } from "@/store/auth";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useClients } from "@/features/clients/hooks/useClients";
import { useCompanies } from "@/features/group_finance/hooks/useGroupFinance";
import { ClientBranchSelector } from "@/features/clients/components/ClientBranchSelector";
import { useAllSubTasks } from "@/features/tasks/hooks/useSubTasks";
import { SubTaskDetailPanel } from "@/features/tasks/components/SubTaskDetailPanel";
import { TASK_STATUS_COLORS, TASK_STATUS_LABELS, PRIORITY_DOT } from "@/features/tasks/types";
import type { SubTask, TaskAssignee } from "@/features/tasks/types";
import { Modal } from "@/components/ui/Modal";
import { ExternalLink, FileText, Pencil, Upload, Zap } from "lucide-react";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { ProjectConversations } from "@/features/enterprises/components/conversations/ProjectConversations";
import { usePageTitle } from "@/hooks/usePageTitle";
import { formatDate } from "@/utils/formatDate";
import { useConversationDeepLink } from "@/hooks/useConversationDeepLink";
import { ConstructionBoard } from "@/features/construction/components/ConstructionBoard";

interface Order {
  id: string; order_number: string; contractor_name: string;
  status: OrderStatus; priority: Priority; deadline: string | null;
}

interface Protocol {
  id: string; protocol_number: string; order_number: string;
  is_final: boolean; is_withdrawn: boolean; accepted_by_contractor: boolean | null; created_at: string;
}

interface Invoice {
  id: string; invoice_number: string; order_number: string;
  amount_gross: string; status: string; payment_status: string;
}

const orderStatusColors: Record<OrderStatus, "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange"> = {
  sent: "blue", accepted: "green", rejected: "red", in_progress: "orange",
  in_progress_partial: "yellow", submitted_for_acceptance: "purple",
  accepted_final: "green", closed: "gray", cancelled: "gray",
};

const invoiceStatusLabel: Record<string, string> = {
  submitted: "Przesłana", accepted: "Przyjęta", rejected: "Nieprzyjęta",
};
const paymentLabel: Record<string, string> = {
  unpaid: "Nieopłacona", partially_paid: "Częściowo opłacona", paid: "Opłacona",
};

/** Pull a human-readable message out of a failed project-name PATCH (envelope: { errors }). */
function nameErrorMessage(err: unknown): string {
  const errs = (err as { response?: { data?: { errors?: Record<string, unknown> } } })?.response?.data?.errors;
  const fieldMsg = Array.isArray(errs?.name) ? (errs?.name as string[])[0] : undefined;
  const detail = typeof errs?.detail === "string" ? errs.detail : undefined;
  return fieldMsg || detail || "Nie udało się zmienić nazwy.";
}

/** Komunikat z nieudanego pobrania umowy (koperta: { errors: { detail } }). */
function contractErrorMessage(err: unknown): string {
  const errs = (err as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors;
  return errs?.detail || "Nie udało się pobrać umowy.";
}

/** Komunikat z nieudanego wgrania umowy (koperta: { errors: { detail | file[] } }). */
function contractUploadErrorMessage(err: unknown): string {
  const errs = (err as { response?: { data?: { errors?: { detail?: string; file?: string[] } } } })
    ?.response?.data?.errors;
  return errs?.detail || errs?.file?.[0] || "Nie udało się wgrać umowy.";
}

export function ProjectDashboardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const isCeo = Boolean(user?.is_ceo);
  const isTechnicalDirector = isDirector && Boolean(user?.is_technical_director);
  // Roster + amount permissions: CEO / dyrektor techniczny only.
  const canManageCoordinators = isCeo || isTechnicalDirector;
  // Umowę wgrywa ten sam krąg osób, co dotąd wgrywał ją przy „oznacz przetarg jako
  // wygrany" (mark-won = IsTechnicalDirector) — podmiana nie może być łatwiejsza niż
  // pierwsze wgranie.
  const canManageContract = isCeo || isTechnicalDirector;
  // Przejście „przedsięwzięcie → projekt ZE". Bramkowane tak samo jak trasa
  // /power-projects (DLCModuleGuard): bez modułu DLC_01 link i tak odbiłby
  // użytkownika na stronę główną. Podwykonawca tej strony nie otworzy (RoleGuard),
  // więc roli nie sprawdzamy drugi raz.
  const canOpenZeProject = Boolean(user?.accessible_dlc_modules?.includes("dlc_01"));
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [editingClient, setEditingClient] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [editingBranch, setEditingBranch] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [selectedSubBranchId, setSelectedSubBranchId] = useState<string | null>(null);
  const [editingCoordinators, setEditingCoordinators] = useState(false);
  const [selectedCoordinatorIds, setSelectedCoordinatorIds] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [editingCompany, setEditingCompany] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [contractModal, setContractModal] = useState(false);
  // Pełna edycja przedsięwzięcia — ten sam formularz, co na /projects/:id/edit.
  // Inline-ołówki obok pojedynczych pól zostają; modal jest dla reszty (termin,
  // wartość, grupa, opis), której inline nie obejmuje.
  const [editModal, setEditModal] = useState(false);
  const [contractDraft, setContractDraft] = useState<File[]>([]);
  const qc = useQueryClient();

  const { data: clients = [] } = useClients(
    isCeo || user?.role === "coordinator" ||
    (user?.role === "director" && !!user?.is_technical_director),
  );
  const { data: companies = [] } = useCompanies(
    isCeo || user?.role === "accountant" || !!user?.has_group_finance_access,
  );

  const { data: allInternalUsers = [] } = useQuery({
    queryKey: ["internal-users"],
    queryFn: () =>
      apiClient
        .get<{ data: Array<{ id: string; first_name: string; last_name: string; full_name: string; role: string }> }>("/users/")
        .then((r) => r.data.data),
    enabled: canManageCoordinators,
  });

  const coordinatorUsers = allInternalUsers.filter((u) => u.role === "coordinator" || u.role === "director");

  const { data: project, isLoading } = useQuery({
    queryKey: ["projects", id],
    queryFn: () =>
      apiClient
        .get<{ data: Project[] }>("/projects/", { params: { project: id } })
        .then((r) => r.data.data[0] ?? null),
    enabled: !!id,
  });
  usePageTitle(project?.name);
  // Powiadomienie o wiadomości/oznaczeniu w konwersacji przedsięwzięcia prowadzi
  // do sekcji „Konwersacje" na dole pulpitu, nie na jego górę.
  const { ref: conversationsRef } = useConversationDeepLink<HTMLDivElement>(
    !isLoading && !!project,
  );

  // Edit access mirrors the edit page: CEO, technical director, or a coordinator
  // assigned to this project. Company stays CEO-only; the roster stays CEO/tech-director.
  const isAssignedCoordinator =
    user?.role === "coordinator" && !!user && Boolean(project?.coordinators.includes(user.id));
  const canEdit = isCeo || isTechnicalDirector || isAssignedCoordinator;
  const canEditName = canEdit;

  const { data: orders } = useQuery({
    queryKey: ["orders", { project: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Order[] }>("/orders/", { params: { project: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: protocols } = useQuery({
    queryKey: ["protocols", { project: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Protocol[] }>("/protocols/", { params: { project: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: invoices } = useQuery({
    queryKey: ["invoices", { project: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Invoice[] }>("/invoices/", { params: { project: id } })
        .then((r) => r.data.data),
    enabled: !!id && !!project?.has_finance_access,
  });

  const updateNameMutation = useMutation({
    mutationFn: (name: string) => apiClient.patch(`/projects/${id}/`, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      setEditingName(false);
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: (clientId: string | null) =>
      apiClient.patch(`/projects/${id}/`, { client: clientId, client_branch: null, client_sub_branch: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      setEditingClient(false);
    },
  });

  const updateBranchMutation = useMutation({
    mutationFn: (data: { client_branch: string | null; client_sub_branch: string | null }) =>
      apiClient.patch(`/projects/${id}/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      setEditingBranch(false);
    },
  });

  const updateCoordinatorsMutation = useMutation({
    mutationFn: (coordinatorIds: string[]) =>
      apiClient.patch(`/projects/${id}/`, { coordinators: coordinatorIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      setEditingCoordinators(false);
    },
  });

  const setCompanyMutation = useMutation({
    mutationFn: (companyId: string) =>
      apiClient.patch(`/projects/${id}/set-company/`, { company: companyId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      setEditingCompany(false);
    },
  });

  // Umowa leży na przetargu źródłowym; backend podpisuje link dopiero na klik,
  // więc nie da się go wkleić w <a href> — pobieramy po odpowiedzi.
  const downloadContract = useMutation({
    mutationFn: () =>
      apiClient
        .get<{ data: { url: string; filename: string } }>(`/projects/${id}/contract/`)
        .then((r) => r.data.data),
    onSuccess: ({ url, filename }) => triggerBrowserDownload(url, filename),
  });

  // Podpięcie/podmiana umowy. Przy PODMIANIE backend zakłada wpis „historia" w
  // konwersacji zarządu (stary + nowy plik w załącznikach) — stąd unieważnienie
  // wiadomości kanału „board" obok samego przedsięwzięcia.
  const uploadContract = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return apiClient.post(`/projects/${id}/contract/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project-messages", id, "board"] });
      setContractModal(false);
      setContractDraft([]);
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: () => apiClient.delete(`/projects/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      navigate("/projects");
    },
  });

  if (isLoading) return <PageLoader />;
  if (!project) return <p className="text-gray-500 dark:text-gray-400">Nie znaleziono przedsięwzięcia.</p>;

  const activeOrders = (orders ?? []).filter(
    (o) => !["closed", "cancelled"].includes(o.status)
  ).length;
  const totalInvoiceValue = (invoices ?? []).reduce(
    (sum, inv) => sum + parseFloat(inv.amount_gross),
    0
  );

  const commitName = () => {
    const next = nameDraft.trim();
    if (next && next !== project.name) updateNameMutation.mutate(next);
    else setEditingName(false);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-bold text-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] px-2 py-1 rounded">
              {project.short_name}
            </span>
            {/* Los umowy wypiera zwykły status: po wypowiedzeniu zamiast „Aktywne"
                widać „W trakcie wypowiedzenia" / „Wypowiedziana (data)". */}
            {"status" in project && <ProjectStatusBadge project={project as Project} />}
            {(project as Project).linked_tender_id && (
              <Link
                to={`/tenders/${(project as Project).linked_tender_id}`}
                className="text-xs text-purple-600 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 px-2 py-0.5 rounded-full hover:bg-purple-100 transition-colors"
              >
                ← Przetarg
              </Link>
            )}
          </div>
          <div>
            {editingName ? (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitName();
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    disabled={updateNameMutation.isPending}
                    maxLength={255}
                    className="text-base sm:text-2xl font-bold text-gray-900 dark:text-gray-100 bg-transparent border-b-2 border-[var(--color-primary)] focus:outline-none flex-1 min-w-[260px]"
                  />
                  <button
                    onClick={commitName}
                    disabled={updateNameMutation.isPending}
                    className="text-xs px-2 py-1 bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
                  >
                    Zapisz
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-400"
                  >
                    Anuluj
                  </button>
                </div>
                {updateNameMutation.isError && (
                  <p className="text-xs text-red-600">{nameErrorMessage(updateNameMutation.error)}</p>
                )}
              </div>
            ) : (
              <h1 className="text-base sm:text-2xl font-bold text-gray-900 dark:text-gray-100 break-words flex items-start gap-2">
                <span>{project.name}</span>
                {canEditName && (
                  <button
                    onClick={() => {
                      setNameDraft(project.name);
                      setEditingName(true);
                    }}
                    className="mt-1 text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] px-1 shrink-0"
                    title="Zmień nazwę przedsięwzięcia"
                  >
                    ✎
                  </button>
                )}
              </h1>
            )}
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3 sm:mb-6" />
          </div>

          <dl className="mt-4 sm:mt-5 flex flex-col divide-y divide-gray-100 dark:divide-gray-700/60 border-y border-gray-100 dark:border-gray-700/60">
          {/* Client selector */}
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-x-3 gap-y-1 py-2">
            <dt className="w-28 shrink-0 text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">Klient</dt>
            <dd className="flex-1 min-w-0">
          {editingClient ? (
            <div className="flex items-center gap-2 flex-wrap">
              <SearchableSelect
                placeholder="— brak klienta —"
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                options={clients.map((c) => ({ value: c.id, label: c.name }))}
                className="text-sm"
              />
              <button
                onClick={() => updateClientMutation.mutate(selectedClientId || null)}
                disabled={updateClientMutation.isPending}
                className="text-xs px-2 py-1 bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
              >
                Zapisz
              </button>
              <button
                onClick={() => setEditingClient(false)}
                className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-400"
              >
                Anuluj
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
              {project.client_detail ? (
                <Link
                  to={`/clients/${project.client_detail.id}`}
                  className="text-gray-700 dark:text-gray-300 font-medium hover:text-[var(--color-primary)] hover:underline text-sm"
                >
                  {project.client_detail.name}
                </Link>
              ) : (
                <span className="text-gray-400 dark:text-gray-500 text-sm">{project.client_name || "— brak klienta —"}</span>
              )}
              {canEdit && (
                <button
                  onClick={() => {
                    setSelectedClientId(project.client ?? "");
                    setEditingClient(true);
                  }}
                  className="ml-1 text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] px-1"
                  title="Zmień klienta"
                >
                  ✎
                </button>
              )}
            </div>
          )}
            </dd>
          </div>
          {/* Branch / sub-branch */}
          {(project.client_branch_detail || project.client_sub_branch_detail || editingBranch || (canEdit && project.client)) && (
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-x-3 gap-y-1 py-2">
            <dt className="w-28 shrink-0 text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">Lokalizacja</dt>
            <dd className="flex-1 min-w-0">
          {editingBranch ? (
            <div className="flex flex-col gap-2">
              <ClientBranchSelector
                clientId={project.client ?? null}
                selectedBranch={selectedBranchId}
                selectedSubBranch={selectedSubBranchId}
                onBranchChange={(id) => { setSelectedBranchId(id); setSelectedSubBranchId(null); }}
                onSubBranchChange={setSelectedSubBranchId}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => updateBranchMutation.mutate({ client_branch: selectedBranchId, client_sub_branch: selectedSubBranchId })}
                  disabled={updateBranchMutation.isPending}
                  className="text-xs px-2 py-1 bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
                >
                  Zapisz
                </button>
                <button
                  onClick={() => setEditingBranch(false)}
                  className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-400"
                >
                  Anuluj
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-sm text-gray-700 dark:text-gray-300">
              {project.client_branch_detail ? (
                <span>{project.client_branch_detail.display_name}</span>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">— brak —</span>
              )}
              {project.client_sub_branch_detail ? (
                <span className="text-gray-400 dark:text-gray-500">→ {project.client_sub_branch_detail.display_name}</span>
              ) : null}
              {canEdit && project.client && (
                <button
                  onClick={() => {
                    setSelectedBranchId(project.client_branch ?? null);
                    setSelectedSubBranchId(project.client_sub_branch ?? null);
                    setEditingBranch(true);
                  }}
                  className="ml-1 text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] px-1"
                  title="Zmień oddział"
                >
                  ✎
                </button>
              )}
            </div>
          )}
            </dd>
          </div>
          )}

          {/* Performing company */}
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-x-3 gap-y-1 py-2">
            <dt className="w-28 shrink-0 text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">Realizuje</dt>
            <dd className="flex-1 min-w-0">
          {editingCompany ? (
            <div className="flex flex-col gap-2">
              <SearchableSelect
                placeholder="— wybierz spółkę —"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                options={[...companies]
                  .sort((a, b) => a.short_code.localeCompare(b.short_code))
                  .map((c) => ({ value: c.id, label: `${c.short_code} — ${c.name}` }))}
                className="text-sm"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => selectedCompanyId && setCompanyMutation.mutate(selectedCompanyId)}
                  disabled={!selectedCompanyId || setCompanyMutation.isPending}
                  className="text-xs px-2 py-1 bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
                >
                  Zapisz
                </button>
                <button
                  onClick={() => setEditingCompany(false)}
                  className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-400"
                >
                  Anuluj
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-sm">
              {project.company_name ? (
                <span className="font-medium text-gray-700 dark:text-gray-300">{project.company_name}</span>
              ) : (
                <span className="text-gray-400 dark:text-gray-500 italic text-xs">nie ustawiono</span>
              )}
              {isCeo && (
                <button
                  onClick={() => {
                    setSelectedCompanyId(project.company ?? "");
                    setEditingCompany(true);
                  }}
                  className="ml-1 text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] px-1"
                  title="Zmień spółkę realizującą"
                >
                  ✎
                </button>
              )}
            </div>
          )}
            </dd>
          </div>

          {/* Coordinators */}
          {editingCoordinators ? (
            <div className="my-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/40 p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Koordynatorzy projektu</p>
                {coordinatorUsers.length > 0 && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Wybrano {selectedCoordinatorIds.length} z {coordinatorUsers.length}
                  </span>
                )}
              </div>
              {coordinatorUsers.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Brak użytkowników z rolą koordynatora.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {coordinatorUsers.map((u) => {
                    const checked = selectedCoordinatorIds.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className={`flex items-center gap-2 text-sm cursor-pointer select-none rounded-md border px-2.5 py-1.5 transition-colors ${
                          checked
                            ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCoordinatorIds((prev) => [...prev, u.id]);
                            } else {
                              setSelectedCoordinatorIds((prev) => prev.filter((x) => x !== u.id));
                            }
                          }}
                          className="accent-[var(--color-primary)] shrink-0"
                        />
                        <span className="truncate">{u.full_name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-2 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => updateCoordinatorsMutation.mutate(selectedCoordinatorIds)}
                  disabled={updateCoordinatorsMutation.isPending}
                  className="text-xs px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-md font-medium hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
                >
                  Zapisz
                </button>
                <button
                  onClick={() => setEditingCoordinators(false)}
                  className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-400"
                >
                  Anuluj
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-x-3 gap-y-1 py-2">
              <dt className="w-28 shrink-0 text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">Koordynator</dt>
              <dd className="flex-1 min-w-0 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              {project.coordinators_detail.length > 0 ? (
                project.coordinators_detail.map((c, i) => (
                  <span
                    key={c.id}
                    className="text-sm text-gray-700 dark:text-gray-300"
                    title={
                      c.since
                        ? `${c.since_exact ? "Koordynator od" : "Koordynator co najmniej od"} ${new Date(c.since).toLocaleDateString("pl")}`
                        : undefined
                    }
                  >
                    {c.first_name} {c.last_name}
                    {c.since && c.since_exact && (
                      <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">
                        od {new Date(c.since).toLocaleDateString("pl")}
                      </span>
                    )}
                    {i < project.coordinators_detail.length - 1 ? "," : ""}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-400 dark:text-gray-500">— brak —</span>
              )}
              {canManageCoordinators && (
                <button
                  onClick={() => {
                    setSelectedCoordinatorIds(project.coordinators);
                    setEditingCoordinators(true);
                  }}
                  className="ml-1 text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] px-1"
                  title="Zmień koordynatorów"
                >
                  ✎
                </button>
              )}
              </dd>
            </div>
          )}
          {/* Status — wstrzymanie/wznowienie: CEO / dyrektor techniczny / przypisany koordynator;
              dezaktywacja: CEO lub dyr. techniczny z dostępem finansowym. Księgowość tylko czyta.
              Koordynator bez dostępu do kwot nie wchodzi na kartę finansów, więc status zmienia stąd. */}
          {canSetProjectStatus(user, project) && (
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-x-3 gap-y-1 py-2">
              <dt className="w-28 shrink-0 text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">Status</dt>
              <dd className="flex-1 min-w-0">
                <ProjectStatusActions project={project} canDeactivate={canDeactivateProject(user, project)} />
              </dd>
            </div>
          )}
          </dl>

          {project.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 max-w-2xl">{project.description}</p>
          )}
          {(() => {
            const p = project as Project;
            const isDirector = (user as { role?: string } | null)?.role === "director";
            const ceoDate = isCeo ? p.planned_end_date_ceo : null;
            const directorDate = (isCeo || isDirector) ? p.planned_end_date_director : null;
            const displayDate = ceoDate ?? directorDate ?? p.planned_end_date;
            // Projekt ZE jest osobnym powodem, żeby ta linijka istniała — przedsięwzięcie
            // bez terminu też ma dokąd prowadzić.
            const showZeJump = Boolean(project.has_ze_project) && canOpenZeProject;
            if (!displayDate && !showZeJump) return null;
            return (
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                {!displayDate ? null : ceoDate ? (
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded uppercase tracking-wide">CEO</span>
                    Planowane zakończenie: {formatDate(ceoDate)}
                    {directorDate && directorDate !== ceoDate && (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded uppercase tracking-wide">DYR</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">{formatDate(directorDate)}</span>
                      </span>
                    )}
                    {p.planned_end_date && p.planned_end_date !== ceoDate && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-normal ml-1">(publ. {formatDate(p.planned_end_date)})</span>
                    )}
                  </span>
                ) : directorDate ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded uppercase tracking-wide">DYR</span>
                    Planowane zakończenie: {formatDate(directorDate)}
                    {p.planned_end_date && p.planned_end_date !== directorDate && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-normal ml-1">(publ. {formatDate(p.planned_end_date)})</span>
                    )}
                  </span>
                ) : (
                  <>Planowane zakończenie: {formatDate(displayDate)}</>
                )}
                {/* Wartość widzi też każdy z prawem „Przeglądanie przetargów", gdy projekt
                    powstał z wygranego przetargu — kwotę wpisano przy mark-won i ta sama
                    osoba może ją już dziś wyczytać z PDF-a umowy (przycisk obok). Dyrektor
                    techniczny widzi ją zawsze — ta sama reguła co wartość w workspace ZE
                    (enterprises.utils.project_amount_access_scope). */}
                {(project.has_finance_access || isTechnicalDirector ||
                  (Boolean(user?.has_view_tenders_permission) && !!project.linked_tender_id)) &&
                  p.total_value && (
                  <span className="ml-3">
                    Wartość: <strong className="text-gray-600 dark:text-gray-400">{formatAmount(p.total_value)} PLN</strong>
                  </span>
                )}
                {/* Przedsięwzięcie prowadzone jako projekt ZE — wejście do workspace'u
                    DLC_01 (trasa kluczuje po id przedsięwzięcia, nie po id profilu ZE). */}
                {showZeJump && (
                  <Link
                    to={`/power-projects/${id}`}
                    title="Przejdź do projektu ZE"
                    className={clsx(
                      "inline-flex items-center gap-1.5 text-[var(--color-primary)] hover:underline",
                      displayDate && "ml-4",
                    )}
                  >
                    <Zap size={13} className="shrink-0" />
                    Projekt ZE
                    <ExternalLink size={12} className="shrink-0" />
                  </Link>
                )}
              </p>
            );
          })()}
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            {project.has_contract_file && (
              <button
                onClick={() => downloadContract.mutate()}
                disabled={downloadContract.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 transition-colors"
                title="Pobierz umowę z przetargu"
              >
                <FileText size={14} /> {downloadContract.isPending ? "Pobieranie…" : "Pobierz umowę"}
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => setEditModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                title="Edytuj dane przedsięwzięcia"
              >
                <Pencil size={14} /> Edytuj
              </button>
            )}
            {canManageContract && (
              <button
                onClick={() => { setContractDraft([]); uploadContract.reset(); setContractModal(true); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                title={project.has_contract_file ? "Wgraj nową wersję umowy" : "Podepnij umowę do przedsięwzięcia"}
              >
                <Upload size={14} /> {project.has_contract_file ? "Podmień umowę" : "Wgraj umowę"}
              </button>
            )}
            {project.has_finance_access && (
              <Link
                to={`/projects/${id}/finance`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium whitespace-nowrap hover:bg-[var(--color-primary-hover)] transition-colors"
              >
                Analiza finansowa →
              </Link>
            )}
          </div>
          {downloadContract.isError && (
            <p className="text-xs text-red-600">{contractErrorMessage(downloadContract.error)}</p>
          )}
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatCard label="Zamówień aktywnych" value={String(activeOrders)} />
        <StatCard label="Zamówień łącznie" value={String((orders ?? []).length)} />
        <StatCard label="Protokołów POCK" value={String((protocols ?? []).filter((p) => !p.is_withdrawn).length)} />
        {project.has_finance_access && (
          <StatCard
            label="Wartość faktur brutto"
            value={totalInvoiceValue > 0 ? `${formatAmount(totalInvoiceValue)} PLN` : "—"}
          />
        )}
      </div>

      {/* Aneksy, wypowiedzenie, porozumienie — ten sam krąg czyta je, co umowę
          (prawo „Przeglądanie przetargów"); wgrywa dyrektor techniczny / zarząd. */}
      {(canManageContract || Boolean(user?.has_view_tenders_permission)) && (
        <ContractDocumentsSection
          project={project}
          canManage={canManageContract}
          showAmounts={Boolean(project.has_finance_access) || isTechnicalDirector}
        />
      )}

      {/* Etapy budowy — tylko dla przedsięwzięć budowlanych. Lekka plansza: graf
          zależności, trzy stany, termin. Bez faz, SLA i doradcy AI. */}
      {project.work_scope === "budowa" && (
        <Section title="Etapy budowy">
          <ConstructionBoard projectId={project.id} canEdit={canEdit} />
        </Section>
      )}

      {/* Orders */}
      <Section title="Zamówienia">
        {(orders ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak zamówień.</p>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-2">
              {(orders ?? []).map((o) => (
                <div key={o.id} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                  <Link to={`/orders/${o.id}`} className="font-medium text-sm text-[var(--color-primary)] hover:underline block">{o.order_number}</Link>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{o.contractor_name}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Badge color={orderStatusColors[o.status]}>{ORDER_STATUS_LABELS[o.status]}</Badge>
                    <Badge color={o.priority === "high" ? "red" : o.priority === "low" ? "gray" : "blue"}>{PRIORITY_LABELS[o.priority]}</Badge>
                  </div>
                  {o.deadline && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Termin: {formatDate(o.deadline)}</p>}
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs min-w-[600px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr zamówienia</th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Podwykonawca</th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Priorytet</th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Termin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(orders ?? []).map((o) => (
                    <tr key={o.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 font-medium text-[var(--color-primary)]">
                        <Link to={`/orders/${o.id}`} className="hover:underline">{o.order_number}</Link>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{o.contractor_name}</td>
                      <td className="px-4 py-3">
                        <Badge color={orderStatusColors[o.status]}>{ORDER_STATUS_LABELS[o.status]}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={o.priority === "high" ? "red" : o.priority === "low" ? "gray" : "blue"}>{PRIORITY_LABELS[o.priority]}</Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(o.deadline)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Section>

      {/* Protocols */}
      <Section title="Protokoły POCK">
        {(protocols ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak protokołów.</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[600px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr protokołu</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówienie</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Typ</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Akceptacja</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(protocols ?? []).map((p) => (
                  <tr key={p.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${p.is_withdrawn ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3 font-medium text-[var(--color-primary)]">
                      <Link to={`/protocols/${p.id}`} className={`hover:underline ${p.is_withdrawn ? "line-through" : ""}`}>
                        {p.protocol_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{p.order_number}</td>
                    <td className="px-4 py-3">
                      {p.is_withdrawn ? (
                        <Badge color="gray">Wycofany</Badge>
                      ) : (
                        <Badge color={p.is_final ? "purple" : "blue"}>
                          {p.is_final ? "Końcowy" : "Częściowy"}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!p.is_withdrawn && (
                        <Badge
                          color={
                            p.accepted_by_contractor === null
                              ? "gray"
                              : p.accepted_by_contractor
                              ? "green"
                              : "red"
                          }
                        >
                          {p.accepted_by_contractor === null
                            ? "Oczekuje"
                            : p.accepted_by_contractor
                            ? "Zaakceptowany"
                            : "Odrzucony"}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {new Date(p.created_at).toLocaleDateString("pl")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Invoices */}
      <Section title="Faktury P-GK">
        {(invoices ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak faktur.</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[600px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr faktury</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówienie</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Brutto</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Płatność</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(invoices ?? []).map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-medium text-[var(--color-primary)]">
                      <Link to={`/invoices/${inv.id}`} className="hover:underline">{inv.invoice_number}</Link>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{inv.order_number}</td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatAmount(inv.amount_gross)} PLN
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        color={
                          inv.status === "accepted"
                            ? "green"
                            : inv.status === "rejected"
                            ? "red"
                            : "blue"
                        }
                      >
                        {invoiceStatusLabel[inv.status] ?? inv.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        color={
                          inv.payment_status === "paid"
                            ? "green"
                            : inv.payment_status === "partially_paid"
                            ? "yellow"
                            : "gray"
                        }
                      >
                        {paymentLabel[inv.payment_status] ?? inv.payment_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Conversations */}
      {user && (
        <div ref={conversationsRef} className="scroll-mt-4">
          <ProjectConversations
            projectId={id!}
            currentUserId={user.id}
            isDirector={isDirector}
            isCeo={isCeo}
          />
        </div>
      )}

      {/* Task analysis */}
      <ProjectTaskAnalysis project={project} projectId={id!} />

      {/* Danger zone — delete */}
      {canManageCoordinators && (
        <div className="mt-12 pt-6 border-t border-gray-100 dark:border-gray-800 flex justify-end">
          <button
            onClick={() => { setDeleteInput(""); setDeleteConfirm(true); }}
            className="text-xs text-gray-400 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-500 transition-colors underline underline-offset-2"
          >
            Usuń przedsięwzięcie
          </button>
        </div>
      )}

      {/* Pełna edycja przedsięwzięcia — dokładnie ten sam formularz (i te same pola),
          co strona /projects/:id/edit; wchodzi się w nią stąd, bez opuszczania pulpitu. */}
      <Modal
        open={editModal}
        onClose={() => setEditModal(false)}
        title="Edytuj przedsięwzięcie"
        size="lg"
      >
        <ProjectEditForm
          project={project}
          onSaved={() => setEditModal(false)}
          onCancel={() => setEditModal(false)}
          variant="modal"
        />
      </Modal>

      {/* Umowa — podpięcie / podmiana pliku */}
      {contractModal && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {project.has_contract_file ? "Podmień umowę" : "Wgraj umowę"}
            </h2>
            {project.has_contract_file && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Dotychczasowa umowa nie zniknie — trafi razem z nową do konwersacji zarządu
                jako historia podmiany.
              </p>
            )}
            <FileDropZone
              files={contractDraft}
              onFilesChange={setContractDraft}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              disabled={uploadContract.isPending}
              label="Przeciągnij plik umowy albo kliknij, żeby wybrać"
            />
            {uploadContract.isError && (
              <p className="text-xs text-red-600">{contractUploadErrorMessage(uploadContract.error)}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setContractModal(false)}
                disabled={uploadContract.isPending}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-40"
              >
                Anuluj
              </button>
              <button
                onClick={() => contractDraft[0] && uploadContract.mutate(contractDraft[0])}
                disabled={!contractDraft[0] || uploadContract.isPending}
                className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {uploadContract.isPending ? "Wgrywanie..." : "Zapisz umowę"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Usuń przedsięwzięcie</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Ta operacja jest <strong>nieodwracalna</strong>. Zostaną usunięte wszystkie powiązane dane (zamówienia, protokoły, faktury, zadania).
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Aby potwierdzić, wpisz:{" "}
              <strong className="text-gray-900 dark:text-gray-100 font-mono">usuń przedsięwzięcie</strong>
            </p>
            <input
              autoFocus
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="usuń przedsięwzięcie"
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                Anuluj
              </button>
              <button
                onClick={() => deleteProjectMutation.mutate()}
                disabled={deleteInput !== "usuń przedsięwzięcie" || deleteProjectMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleteProjectMutation.isPending ? "Usuwanie..." : "Usuń bezpowrotnie"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 transition-colors hover:border-gray-300 dark:hover:border-gray-600">
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{title}</h2>
      {children}
    </div>
  );
}

function daysAgo(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
}

function PersonInitials({ assignee }: { assignee: TaskAssignee }) {
  const initials = assignee.full_name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  if (assignee.profile_picture_url)
    return <img src={assignee.profile_picture_url} alt={assignee.full_name} className="w-8 h-8 object-cover flex-shrink-0" />;
  return (
    <div className="w-8 h-8 bg-[var(--color-primary)] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 select-none">
      {initials || "?"}
    </div>
  );
}

function ProjectTaskAnalysis({ project, projectId }: { project: Project; projectId: string }) {
  const navigate = useNavigate();
  const subtaskFilters = useMemo(() => ({ project: [projectId] }), [projectId]);
  const { data: subtasks = [], isLoading } = useAllSubTasks(subtaskFilters);
  const [subtaskDetail, setSubtaskDetail] = useState<{ taskId: string; subtaskId: string } | null>(null);

  // Group active subtasks by assignee
  const byAssignee = useMemo(() => {
    const map = new Map<string, { assignee: TaskAssignee; subtasks: SubTask[] }>();
    for (const st of subtasks) {
      if (["done", "cancelled"].includes(st.status)) continue;
      for (const a of st.assignees) {
        if (!map.has(a.id)) map.set(a.id, { assignee: a, subtasks: [] });
        map.get(a.id)!.subtasks.push(st);
      }
    }
    return Array.from(map.values()).sort((a, b) => b.subtasks.length - a.subtasks.length);
  }, [subtasks]);

  const totalActive = subtasks.filter((s) => !["done", "cancelled"].includes(s.status)).length;
  const totalDone = subtasks.filter((s) => s.status === "done").length;

  return (
    <Section title="Analiza zadań i podzadań">
      {/* Coordinators */}
      {project.coordinators_detail.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-5 p-3 bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] rounded-xl border border-[var(--color-primary)] dark:border-[var(--color-primary)]">
          <span className="text-xs font-semibold text-[var(--color-primary)] dark:text-[var(--color-primary)] uppercase tracking-wide shrink-0">
            Kierownik projektu
          </span>
          <div className="flex flex-wrap gap-3">
            {project.coordinators_detail.map((c) => (
              <span key={c.id} className="flex items-center gap-1.5 text-sm font-semibold text-[var(--color-primary)]">
                <UserAvatar
                  fullName={`${c.first_name} ${c.last_name}`}
                  size="sm"
                  initialsClassName="bg-[var(--color-primary)] text-white"
                />
                {c.first_name} {c.last_name}
              </span>
            ))}
          </div>
          <div className="sm:ml-auto flex gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span><strong className="text-gray-700 dark:text-gray-300">{totalActive}</strong> aktywnych</span>
            <span><strong className="text-gray-700 dark:text-gray-300">{totalDone}</strong> ukończonych</span>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Ładowanie…</p>
      ) : byAssignee.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak aktywnych podzadań przypisanych do tego przedsięwzięcia.</p>
      ) : (
        <div className="space-y-4">
          {byAssignee.map(({ assignee, subtasks: userSubs }) => (
            <div
              key={assignee.id}
              className="bg-white dark:bg-gray-800 overflow-hidden"
            >
              {/* Person header */}
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <PersonInitials assignee={assignee} />
                <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">{assignee.full_name}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                  {getRoleLabel(assignee.role, assignee.is_ceo)}
                </span>
                <span className="ml-auto text-xs font-medium text-gray-500 dark:text-gray-400">
                  {userSubs.length} {userSubs.length === 1 ? "podzadanie" : userSubs.length < 5 ? "podzadania" : "podzadań"}
                </span>
              </div>

              {/* Subtask rows */}
              <table className="w-full text-xs">
                <thead className="bg-gray-50/60 dark:bg-gray-800/30">
                  <tr className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-2 w-5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                    <th className="px-2 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Podzadanie</th>
                    <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                    <th className="px-4 py-2 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Termin</th>
                    <th className="px-4 py-2 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Czas trwania</th>
                    <th className="px-3 py-2 w-8 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {userSubs.map((st) => {
                    const days = daysAgo(st.created_at);
                    const isOverdue = st.due_date && new Date(st.due_date) < new Date();
                    return (
                      <tr
                        key={st.id}
                        onClick={() => setSubtaskDetail({ taskId: st.task, subtaskId: st.id })}
                        className="hover:bg-[var(--color-primary-dim)] dark:hover:bg-[var(--color-primary-dim)] cursor-pointer transition-colors group"
                      >
                        <td className="px-4 py-2.5 w-5">
                          <div className={clsx("w-1.5 h-1.5 rounded-full", PRIORITY_DOT[st.priority])} />
                        </td>
                        <td className="px-2 py-2.5 max-w-xs">
                          <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{st.title}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{st.task_title}</p>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={clsx("text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap", TASK_STATUS_COLORS[st.status])}>
                            {TASK_STATUS_LABELS[st.status]}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          {st.due_date ? (
                            <span className={clsx("text-xs font-medium", isOverdue ? "text-red-500" : "text-gray-600 dark:text-gray-300")}>
                              {formatDate(st.due_date)}
                              {isOverdue && <span className="ml-1 text-red-400">↑</span>}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <span className={clsx(
                            "text-xs font-medium px-2 py-0.5 rounded-full",
                            days >= 14 ? "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400"
                            : days >= 7 ? "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-500"
                            : "bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400",
                          )}>
                            od {days} {days === 1 ? "dnia" : "dni"}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 w-8">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tasks/board?task=${st.task}&subtask=${st.id}`);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)] transition-all"
                            title="Otwórz w tablicy"
                          >
                            <ExternalLink size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {subtaskDetail && createPortal(
        <SubTaskDetailPanel
          taskId={subtaskDetail.taskId}
          subtaskId={subtaskDetail.subtaskId}
          onClose={() => setSubtaskDetail(null)}
        />,
        document.body
      )}
    </Section>
  );
}
